import React from 'react';
import PropTypes from 'prop-types';

import { Badge } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { Table } from 'react-bootstrap';
import dayjs from 'dayjs';

import { backgroundGrey, backgroundGrey1, lightTransparentBlue } from 'lib/css/colors';
import Container from 'components/shared/Container';
import DATA_SOURCE_CATEGORY from 'lib/enums/DataSourceCategory';

import DataSourceRunURLField from 'components/data_sources/single_data_source_view/DataSourceRunURLField';

function SingleDataSourceRunDataTable({ dataSourceRuns = [], dataSource }) {
  const isGDSN = dataSource?.source_type === DATA_SOURCE_CATEGORY.GDSN;

  const renderGDSNContext = run => {
    if (!isGDSN || !run.run_metadata) return '-';

    const manufacturer = run.run_metadata.manufacturer;
    const glns = run.run_metadata.manufacturer_glns;

    return (
      <div>
        {manufacturer && (
          <Badge variant="primary" className="mb-1 mr-1">
            {manufacturer}
          </Badge>
        )}
        {glns && glns.length > 0 && (
          <div>
            {glns.slice(0, 2).map(gln => (
              <Badge key={gln} variant="outline-secondary" className={css(styles.smallBadge)}>
                {gln}
              </Badge>
            ))}
            {glns.length > 2 && (
              <Badge variant="outline-secondary" className={css(styles.smallBadge)}>
                +{glns.length - 2} more
              </Badge>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Container>
      <Table className={css(styles.tableText)}>
        <thead className={css(styles.thead)}>
          <tr>
            <th>Run ID</th>
            <th>Date Modified</th>
            {isGDSN && <th>Manufacturer/GLN</th>}
            <th>Processed</th>
            <th>Errors</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className={css(styles.tbody)}>
          {dataSourceRuns?.length ? (
            dataSourceRuns.map((run, index) => (
              <tr
                key={`${run.id}-${run.isInProgress}`}
                className={run.isInProgress ? css(styles.activeRow) : ''}
              >
                <td>
                  <strong>#{run.id}</strong>
                  {run.isInProgress && (
                    <div>
                      <small className="text-muted">
                        Duration: {dayjs().diff(dayjs(run.date_created), 'minute')}m
                      </small>
                    </div>
                  )}
                </td>
                <td>
                  <div className="date-info">
                    <div className="fw-medium">
                      {run.date_modified
                        ? new Date(run.date_modified).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '-'}
                    </div>
                    {run.date_modified && (
                      <small className="text-muted">
                        {new Date(run.date_modified).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </small>
                    )}
                  </div>
                </td>
                {isGDSN && <td>{renderGDSNContext(run)}</td>}
                <td>{run.run_metadata?.processed_count || 0}</td>
                <td>
                  {run.run_metadata?.error_count > 0 ? (
                    <span className="text-danger">{run.run_metadata.error_count}</span>
                  ) : (
                    0
                  )}
                </td>
                <td>
                  {!run.isInProgress && (
                    <>
                      {run.error_report_url && (
                        <DataSourceRunURLField
                          dataSourceRunId={run.id}
                          errorReport={true}
                          label={'Error'}
                        />
                      )}
                      {!isGDSN && (
                        <>
                          <DataSourceRunURLField dataSourceRunId={run.id} />
                          <DataSourceRunURLField
                            dataSourceRunId={run.id}
                            deltaFile={true}
                            label={'Delta'}
                          />
                        </>
                      )}
                    </>
                  )}
                  {run.isInProgress && <small className="text-muted">Running...</small>}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="100%" className="text-center">
                No data source runs found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Container>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky',
    top: 0,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
  activeRow: {
    backgroundColor: backgroundGrey1,
    borderLeft: `3px solid ${lightTransparentBlue}`,
  },
  smallBadge: {
    fontSize: '0.7rem',
    marginRight: '0.25rem',
    marginBottom: '0.25rem',
  },
});

SingleDataSourceRunDataTable.propTypes = {
  dataSourceRuns: PropTypes.array,
  inProgressRuns: PropTypes.array,
  dataSource: PropTypes.shape({
    source_type: PropTypes.string,
  }),
};

export default SingleDataSourceRunDataTable;
